export type SupportTier = 'padrao' | 'prioritario' | 'vip';

export interface PlanLimits {
  maxDisciplinas: number | null; // null = unlimited
  maxSessoesMes: number | null;  // null = unlimited
  estatisticasAvancadas: boolean;
  cronogramaSemanal: boolean;
  progressoDetalhado: boolean;
  recomendacaoIA: boolean;
  suporte: SupportTier;
}

// Limits per plan ID. null = no active plan (free tier)
const PLAN_LIMITS: Record<string, PlanLimits> = {
  free: {
    maxDisciplinas: 2,
    maxSessoesMes: 10,
    estatisticasAvancadas: false,
    cronogramaSemanal: false,
    progressoDetalhado: false,
    recomendacaoIA: false,
    suporte: 'padrao',
  },
  basico: {
    maxDisciplinas: 5,
    maxSessoesMes: 50,
    estatisticasAvancadas: false,
    cronogramaSemanal: false,
    progressoDetalhado: true,
    recomendacaoIA: false,
    suporte: 'padrao',
  },
  pro: {
    maxDisciplinas: 20,
    maxSessoesMes: null,
    estatisticasAvancadas: true,
    cronogramaSemanal: true,
    progressoDetalhado: true,
    recomendacaoIA: false,
    suporte: 'prioritario',
  },
  premium: {
    maxDisciplinas: null,
    maxSessoesMes: null,
    estatisticasAvancadas: true,
    cronogramaSemanal: true,
    progressoDetalhado: true,
    recomendacaoIA: true,
    suporte: 'vip',
  },
};

export const getPlanLimits = (planId: string | null): PlanLimits => {
  if (!planId || !PLAN_LIMITS[planId]) {
    return PLAN_LIMITS.free;
  }
  return PLAN_LIMITS[planId];
};

export const getPlanName = (planId: string | null): string => {
  const names: Record<string, string> = {
    free: 'Gratuito',
    basico: 'Básico',
    pro: 'Pro',
    premium: 'Premium',
  };
  return names[planId || 'free'] || 'Gratuito';
};

export interface SupportInfo {
  tier: SupportTier;
  slaLabel: string;
  slaHours: number | null; // null = 24/7
  badgeLabel: string;
  whatsapp: boolean;
  whatsappNumber?: string;
  whatsappUrl?: string;
  /** Headline shown above the support actions. */
  headline: string;
  /** Longer description of how the user will be attended. */
  description: string;
  /** Label for the primary "open feedback dialog" CTA. */
  ctaLabel: string;
  /** How the user will receive a response. */
  returnChannel: string;
}

const WHATSAPP_NUMBER = '5527999616049';

export const getSupportInfo = (planId: string | null): SupportInfo => {
  const tier = getPlanLimits(planId).suporte;
  switch (tier) {
    case 'vip':
      return {
        tier,
        slaLabel: 'Atendimento VIP 24/7',
        slaHours: null,
        badgeLabel: 'Suporte VIP',
        whatsapp: true,
        whatsappNumber: '(27) 99961-6049',
        whatsappUrl: `https://wa.me/${WHATSAPP_NUMBER}`,
        headline: 'Atendimento VIP exclusivo',
        description:
          'Como assinante Premium, você tem acesso ao canal exclusivo no WhatsApp, com atendimento 24 horas por dia, 7 dias por semana.',
        ctaLabel: 'Abrir chamado VIP',
        returnChannel:
          'Resposta imediata pelo WhatsApp ou em poucas horas pelo e-mail cadastrado, inclusive em finais de semana.',
      };
    case 'prioritario':
      return {
        tier,
        slaLabel: 'Resposta prioritária em até 24h',
        slaHours: 24,
        badgeLabel: 'Suporte Prioritário',
        whatsapp: false,
        headline: 'Fila prioritária do plano Pro',
        description:
          'Sua solicitação entra na fila prioritária e é tratada antes das mensagens do plano padrão.',
        ctaLabel: 'Abrir chamado prioritário',
        returnChannel:
          'Responderemos por e-mail em até 24 horas em dias úteis, com acompanhamento da nossa equipe.',
      };
    default: {
      const isBasico = planId === 'basico';
      return {
        tier,
        slaLabel: isBasico ? 'Resposta em até 48h' : 'Resposta em até 72h',
        slaHours: isBasico ? 48 : 72,
        badgeLabel: 'Suporte Padrão',
        whatsapp: false,
        headline: isBasico ? 'Suporte do plano Básico' : 'Suporte padrão',
        description: isBasico
          ? 'Como assinante Básico, sua mensagem é respondida em até 48 horas em dias úteis.'
          : 'Envie sua mensagem e responderemos em até 72 horas em dias úteis. Faça upgrade para reduzir o tempo de resposta.',
        ctaLabel: 'Enviar mensagem ao suporte',
        returnChannel: isBasico
          ? 'Responderemos por e-mail em até 48 horas em dias úteis.'
          : 'Responderemos por e-mail em até 72 horas em dias úteis.',
      };
    }
  }
};


